// ─── Widget Section Editor ────────────────────────────────────
// Form-based editors for structured widget sections:
// event-details, timeline, gallery, rsvp, venue, countdown, story

import React from 'react';
import { Plus, Trash2, Clock, Calendar, MapPin, Users, Music, Utensils, Heart, Star, Gift, Camera, Palette, ChevronDown, ChevronUp, ArrowUp, ArrowDown, Upload } from 'lucide-react';
import ResponseCardBackgroundPanel from './ResponseCardBackgroundPanel';
import { resolveRsvpLabels } from '../../lib/utils/rsvpLabels';

const ICON_OPTIONS = [
  { id: 'clock', icon: Clock, label: 'Clock' },
  { id: 'calendar', icon: Calendar, label: 'Calendar' },
  { id: 'map-pin', icon: MapPin, label: 'Location' },
  { id: 'users', icon: Users, label: 'People' },
  { id: 'music', icon: Music, label: 'Music' },
  { id: 'utensils', icon: Utensils, label: 'Food' },
  { id: 'heart', icon: Heart, label: 'Heart' },
  { id: 'star', icon: Star, label: 'Star' },
  { id: 'gift', icon: Gift, label: 'Gift' },
  { id: 'camera', icon: Camera, label: 'Camera' },
];

function FieldLabel({ children }) {
  return <label className="text-[10px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider block mb-1">{children}</label>;
}

function TextInput({ value, onChange, placeholder, ...props }) {
  return (
    <input
      type="text"
      value={value || ''}
      onChange={e => onChange(e.target.value)}
      placeholder={placeholder}
      className="w-full px-2.5 py-1.5 text-xs rounded-md border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 text-gray-800 dark:text-gray-200 outline-none focus:ring-1 focus:ring-blue-400"
      {...props}
    />
  );
}

function TextArea({ value, onChange, placeholder, rows = 3 }) {
  return (
    <textarea
      value={value || ''}
      onChange={e => onChange(e.target.value)}
      placeholder={placeholder}
      rows={rows}
      className="w-full px-2.5 py-1.5 text-xs rounded-md border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 text-gray-800 dark:text-gray-200 outline-none focus:ring-1 focus:ring-blue-400 resize-none"
    />
  );
}

// ─── Event Details Editor ─────────────────────────────────────
function EventDetailsEditor({ data, onChange }) {
  const items = data?.items || [];

  const updateItem = (idx, field, value) => {
    const next = [...items];
    next[idx] = { ...next[idx], [field]: value };
    onChange({ ...data, items: next });
  };

  const addItem = () => {
    onChange({ ...data, items: [...items, { icon: 'clock', label: '', value: '' }] });
  };

  const removeItem = (idx) => {
    onChange({ ...data, items: items.filter((_, i) => i !== idx) });
  };

  return (
    <div className="space-y-3">
      <div>
        <FieldLabel>Section Title</FieldLabel>
        <TextInput value={data?.title} onChange={v => onChange({ ...data, title: v })} placeholder="Event Details" />
      </div>
      <div>
        <FieldLabel>Subtitle</FieldLabel>
        <TextInput value={data?.subtitle} onChange={v => onChange({ ...data, subtitle: v })} placeholder="Join us for our special day" />
      </div>

      <FieldLabel>Details</FieldLabel>
      {items.map((item, idx) => (
        <div key={idx} className="flex gap-2 items-start p-2 rounded-lg bg-gray-50 dark:bg-gray-800/50 border border-gray-100 dark:border-gray-800">
          {/* Icon selector */}
          <select
            value={item.icon || 'clock'}
            onChange={e => updateItem(idx, 'icon', e.target.value)}
            className="w-16 px-1 py-1.5 text-[10px] rounded border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300"
          >
            {ICON_OPTIONS.map(opt => (
              <option key={opt.id} value={opt.id}>{opt.label}</option>
            ))}
          </select>

          <div className="flex-1 space-y-1">
            <TextInput value={item.label} onChange={v => updateItem(idx, 'label', v)} placeholder="Label (e.g. Date)" />
            <TextInput value={item.value} onChange={v => updateItem(idx, 'value', v)} placeholder="Value (e.g. June 15, 2027)" />
            <TextInput value={item.description} onChange={v => updateItem(idx, 'description', v)} placeholder="Description (optional)" />
            <TextInput value={item.mapUrl} onChange={v => updateItem(idx, 'mapUrl', v)} placeholder="Google Maps link (optional)" />
          </div>

          <button onClick={() => removeItem(idx)} className="p-1 text-red-400 hover:text-red-600 mt-1">
            <Trash2 className="w-3 h-3" />
          </button>
        </div>
      ))}

      <button
        onClick={addItem}
        className="w-full py-1.5 rounded-md border border-dashed border-gray-300 dark:border-gray-700 text-[10px] font-medium text-gray-500 hover:text-blue-500 hover:border-blue-400 transition-colors flex items-center justify-center gap-1"
      >
        <Plus className="w-3 h-3" /> Add Detail
      </button>
    </div>
  );
}

// ─── Timeline Editor ──────────────────────────────────────────
function TimelineEditor({ data, onChange }) {
  const events = data?.events || [];

  const updateEvent = (idx, field, value) => {
    const next = [...events];
    next[idx] = { ...next[idx], [field]: value };
    onChange({ ...data, events: next });
  };

  const addEvent = () => {
    onChange({ ...data, events: [...events, { time: '', title: '', description: '' }] });
  };

  const removeEvent = (idx) => {
    onChange({ ...data, events: events.filter((_, i) => i !== idx) });
  };

  return (
    <div className="space-y-3">
      <div>
        <FieldLabel>Section Title</FieldLabel>
        <TextInput value={data?.title} onChange={v => onChange({ ...data, title: v })} placeholder="Our Day" />
      </div>
      <div>
        <FieldLabel>Eyebrow</FieldLabel>
        <TextInput value={data?.eyebrow} onChange={v => onChange({ ...data, eyebrow: v })} placeholder="SCHEDULE" />
      </div>

      <FieldLabel>Events</FieldLabel>
      {events.map((evt, idx) => (
        <div key={idx} className="p-2 rounded-lg bg-gray-50 dark:bg-gray-800/50 border border-gray-100 dark:border-gray-800 space-y-1">
          <div className="flex gap-2">
            <TextInput value={evt.time} onChange={v => updateEvent(idx, 'time', v)} placeholder="3:00 PM" />
            <button onClick={() => removeEvent(idx)} className="p-1 text-red-400 hover:text-red-600">
              <Trash2 className="w-3 h-3" />
            </button>
          </div>
          <TextInput value={evt.title} onChange={v => updateEvent(idx, 'title', v)} placeholder="Ceremony" />
          <TextInput value={evt.description} onChange={v => updateEvent(idx, 'description', v)} placeholder="Join us as we say our vows" />
        </div>
      ))}

      <button
        onClick={addEvent}
        className="w-full py-1.5 rounded-md border border-dashed border-gray-300 dark:border-gray-700 text-[10px] font-medium text-gray-500 hover:text-blue-500 hover:border-blue-400 transition-colors flex items-center justify-center gap-1"
      >
        <Plus className="w-3 h-3" /> Add Event
      </button>
    </div>
  );
}

// ─── Venue Editor ─────────────────────────────────────────────
function VenueEditor({ data, onChange }) {
  return (
    <div className="space-y-3">
      <div>
        <FieldLabel>Venue Name</FieldLabel>
        <TextInput value={data?.name} onChange={v => onChange({ ...data, name: v })} placeholder="The Garden Estate" />
      </div>
      <div>
        <FieldLabel>Address</FieldLabel>
        <TextArea value={data?.address} onChange={v => onChange({ ...data, address: v })} placeholder="123 Garden Lane, City" rows={2} />
      </div>
      <div>
        <FieldLabel>Google Maps URL</FieldLabel>
        <TextInput value={data?.mapUrl} onChange={v => onChange({ ...data, mapUrl: v })} placeholder="https://maps.google.com/..." />
      </div>
      <div>
        <FieldLabel>Directions Note</FieldLabel>
        <TextArea value={data?.directions} onChange={v => onChange({ ...data, directions: v })} placeholder="Parking is available on-site..." rows={2} />
      </div>
    </div>
  );
}

// ─── RSVP Editor ──────────────────────────────────────────────
function RSVPEditor({ data, onChange, variant }) {
  // Phase B Lite — read labels through the canonical resolver so the editor
  // shows whatever value is effective (canonical wins; legacy keys still
  // populate the corresponding canonical field). Writes always go to
  // canonical keys; legacy keys are left untouched in storage but become
  // dead branches on next render.
  const rawLabels = data?.labels || {};
  const labels = resolveRsvpLabels(rawLabels);
  const updateLabel = (key, val) => onChange({ ...data, labels: { ...rawLabels, [key]: val } });
  const [showLabels, setShowLabels] = React.useState(false);
  const isImmersive = variant === 'rsvp-immersive';

  return (
    <div className="space-y-3">
      <div>
        <FieldLabel>Title</FieldLabel>
        <TextInput value={data?.title} onChange={v => onChange({ ...data, title: v })} placeholder="Will You Join Us?" />
      </div>
      <div>
        <FieldLabel>Subtitle</FieldLabel>
        <TextInput value={data?.subtitle} onChange={v => onChange({ ...data, subtitle: v })} placeholder="We'd love to celebrate with you" />
      </div>
      <div>
        <FieldLabel>Deadline</FieldLabel>
        <TextInput value={data?.deadline} onChange={v => onChange({ ...data, deadline: v })} placeholder="Please respond by May 1, 2027" />
      </div>

      {/* Width — Immersive only */}
      {isImmersive && (
        <div className="pt-2 mt-1 border-t border-gray-100 dark:border-gray-800">
          <FieldLabel className="text-[9px] text-blue-500 uppercase tracking-wider mb-2">Width</FieldLabel>
          <div className="flex gap-1.5" data-testid="rsvp-width-toggle">
            {[
              { id: 'full', label: 'Full bleed', desc: 'Edge to edge' },
              { id: 'inset', label: 'Inset card', desc: 'Centered with margin' },
            ].map(opt => {
              const active = (data?.containerWidth === 'inset' ? 'inset' : 'full') === opt.id;
              return (
                <button
                  key={opt.id}
                  onClick={() => onChange({ ...data, containerWidth: opt.id })}
                  className={`flex-1 px-2.5 py-2 rounded-lg border text-left transition-all ${
                    active
                      ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                      : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                  }`}
                  data-testid={`rsvp-width-${opt.id}`}
                >
                  <div className={`text-[11px] font-medium ${active ? 'text-blue-700 dark:text-blue-300' : 'text-gray-700 dark:text-gray-300'}`}>{opt.label}</div>
                  <div className="text-[9px] text-gray-400 mt-0.5">{opt.desc}</div>
                </button>
              );
            })}
          </div>

          {/* Inset card — Radius & Margin sliders (only when inset is active) */}
          {(data?.containerWidth === 'inset') && (
            <div className="mt-3 space-y-2.5" data-testid="rsvp-inset-controls">
              {/* Corner radius */}
              <div>
                <div className="flex items-center justify-between mb-1">
                  <FieldLabel className="text-[9px] text-gray-500 uppercase tracking-wider">Corner radius</FieldLabel>
                  <span className="text-[10px] text-gray-400 font-mono">{(typeof data?.insetRadius === 'number' ? data.insetRadius : 28)}px</span>
                </div>
                <input
                  type="range"
                  min={0}
                  max={48}
                  step={1}
                  value={typeof data?.insetRadius === 'number' ? data.insetRadius : 28}
                  onChange={(e) => onChange({ ...data, insetRadius: parseInt(e.target.value, 10) })}
                  className="w-full h-1 bg-gray-200 dark:bg-gray-700 rounded-lg appearance-none cursor-pointer accent-blue-500"
                  data-testid="rsvp-inset-radius-slider"
                />
              </div>
              {/* Horizontal margin */}
              <div>
                <div className="flex items-center justify-between mb-1">
                  <FieldLabel className="text-[9px] text-gray-500 uppercase tracking-wider">Side margin</FieldLabel>
                  <span className="text-[10px] text-gray-400 font-mono">{(typeof data?.insetMargin === 'number' ? data.insetMargin : 16)}px</span>
                </div>
                <input
                  type="range"
                  min={0}
                  max={48}
                  step={1}
                  value={typeof data?.insetMargin === 'number' ? data.insetMargin : 16}
                  onChange={(e) => onChange({ ...data, insetMargin: parseInt(e.target.value, 10) })}
                  className="w-full h-1 bg-gray-200 dark:bg-gray-700 rounded-lg appearance-none cursor-pointer accent-blue-500"
                  data-testid="rsvp-inset-margin-slider"
                />
              </div>
            </div>
          )}
        </div>
      )}

      {/* Event Info — shared across all variants */}
      <div className="pt-2 mt-1 border-t border-gray-100 dark:border-gray-800">
        <FieldLabel className="text-[9px] text-blue-500 uppercase tracking-wider mb-2">Event info</FieldLabel>
        <div>
          <FieldLabel>Date</FieldLabel>
          <TextInput value={data?.eventDate} onChange={v => onChange({ ...data, eventDate: v })} placeholder="June 18, 2027" />
        </div>
        <div className="mt-2">
          <FieldLabel>Location</FieldLabel>
          <TextInput value={data?.venueName} onChange={v => onChange({ ...data, venueName: v })} placeholder="Villa Balbiano" />
        </div>
        <label className="flex items-center gap-2 mt-2 cursor-pointer">
          <input
            type="checkbox"
            checked={data?.showEventInfo !== false}
            onChange={e => onChange({ ...data, showEventInfo: e.target.checked })}
            className="rounded border-gray-300 text-blue-500 w-3.5 h-3.5"
          />
          <span className="text-[10px] text-gray-500 dark:text-gray-400">Show on confirmation screen</span>
        </label>
      </div>

      {/* Customizable labels */}
      <div>
        <button
          onClick={() => setShowLabels(!showLabels)}
          className="flex items-center gap-1.5 text-[10px] font-semibold text-blue-500 hover:text-blue-600 uppercase tracking-wider"
        >
          {showLabels ? '- Hide' : '+ Customize'} Form Labels
        </button>
      </div>
      {showLabels && (
        <div className="space-y-2 pl-2 border-l-2 border-blue-100 dark:border-blue-900">
          {isImmersive ? (
            <>
              <FieldLabel className="text-[9px] text-blue-500 uppercase tracking-wider mt-1">Step 1 — RSVP Block</FieldLabel>
              <div>
                <FieldLabel>Intro Text</FieldLabel>
                <TextInput value={labels.heading} onChange={v => updateLabel('heading', v)} placeholder="Will you be there?" />
              </div>
              <div>
                <FieldLabel>Name Label</FieldLabel>
                <TextInput value={labels.nameLabel} onChange={v => updateLabel('nameLabel', v)} placeholder="What's your name?" />
              </div>
              <div>
                <FieldLabel>Attend Label</FieldLabel>
                <TextInput value={labels.attendLabel} onChange={v => updateLabel('attendLabel', v)} placeholder="Will you attend?" />
              </div>
              <div className="flex gap-2">
                <div className="flex-1">
                  <FieldLabel>Yes Button</FieldLabel>
                  <TextInput value={labels.yesLabel} onChange={v => updateLabel('yesLabel', v)} placeholder="Yes" />
                </div>
                <div className="flex-1">
                  <FieldLabel>No Button</FieldLabel>
                  <TextInput value={labels.noLabel} onChange={v => updateLabel('noLabel', v)} placeholder="No" />
                </div>
              </div>
              <div>
                <FieldLabel>Guest Count Label</FieldLabel>
                <TextInput value={labels.guestCountLabel} onChange={v => updateLabel('guestCountLabel', v)} placeholder="How many will attend?" />
              </div>
              <div>
                <FieldLabel>Phone Label</FieldLabel>
                <TextInput value={labels.phoneLabel} onChange={v => updateLabel('phoneLabel', v)} placeholder="For day-of updates (optional)" />
              </div>
              <div>
                <FieldLabel>Confirm Button</FieldLabel>
                <TextInput value={labels.submitLabel} onChange={v => updateLabel('submitLabel', v)} placeholder="Confirm" />
              </div>

              <FieldLabel className="text-[9px] text-blue-500 uppercase tracking-wider mt-3">Step 2 — Transition</FieldLabel>
              <div>
                <FieldLabel>Transition Title</FieldLabel>
                <TextInput value={labels.transitionTitle} onChange={v => updateLabel('transitionTitle', v)} placeholder="You're in." />
              </div>
              <div>
                <FieldLabel>Transition Subtitle</FieldLabel>
                <TextInput value={labels.transitionSubtitle} onChange={v => updateLabel('transitionSubtitle', v)} placeholder="We can't wait to have you." />
              </div>

              <FieldLabel className="text-[9px] text-blue-500 uppercase tracking-wider mt-3">Step 3 — Guest Details</FieldLabel>
              <div>
                <FieldLabel>Guests Title</FieldLabel>
                <TextInput value={labels.guestsTitle} onChange={v => updateLabel('guestsTitle', v)} placeholder="Who's joining you?" />
              </div>
              <div>
                <FieldLabel>Guests Subtitle</FieldLabel>
                <TextInput value={labels.guestsSubtitle} onChange={v => updateLabel('guestsSubtitle', v)} placeholder="Just so we know who to expect." />
              </div>

              <FieldLabel className="text-[9px] text-blue-500 uppercase tracking-wider mt-3">Step 4 — Song Suggestion</FieldLabel>
              <div>
                <FieldLabel>Music Title</FieldLabel>
                <TextInput value={labels.musicTitle} onChange={v => updateLabel('musicTitle', v)} placeholder="Before the night begins..." />
              </div>
              <div>
                <FieldLabel>Music Subtitle</FieldLabel>
                <TextInput value={labels.songLabel} onChange={v => updateLabel('songLabel', v)} placeholder="Add a song" />
              </div>
              <div>
                <FieldLabel>Music Hint</FieldLabel>
                <TextInput value={labels.musicHint} onChange={v => updateLabel('musicHint', v)} placeholder="Something you'd love to hear" />
              </div>
              <div>
                <FieldLabel>Song Placeholder</FieldLabel>
                <TextInput value={labels.songPlaceholder} onChange={v => updateLabel('songPlaceholder', v)} placeholder="Song name or artist" />
              </div>

              <FieldLabel className="text-[9px] text-blue-500 uppercase tracking-wider mt-3">Step 5 — Message</FieldLabel>
              <div>
                <FieldLabel>Message Title</FieldLabel>
                <TextInput value={labels.messageLabel} onChange={v => updateLabel('messageLabel', v)} placeholder="A note." />
              </div>
              <div>
                <FieldLabel>Message Placeholder</FieldLabel>
                <TextInput value={labels.messagePlaceholder} onChange={v => updateLabel('messagePlaceholder', v)} placeholder="Say something to them…" />
              </div>

              <FieldLabel className="text-[9px] text-blue-500 uppercase tracking-wider mt-3">Step 6 — Final</FieldLabel>
              <div>
                <FieldLabel>Confirmation Title</FieldLabel>
                <TextInput value={labels.thankYouTitle} onChange={v => updateLabel('thankYouTitle', v)} placeholder="See you there." />
              </div>
              <div>
                <FieldLabel>Confirmation Subtitle</FieldLabel>
                <TextInput value={labels.thankYouAccept} onChange={v => updateLabel('thankYouAccept', v)} placeholder="You're all set." />
              </div>

              <FieldLabel className="text-[9px] text-blue-500 uppercase tracking-wider mt-3">Decline Path</FieldLabel>
              <div>
                <FieldLabel>Note Screen Title</FieldLabel>
                <TextInput value={labels.declineNoteTitle} onChange={v => updateLabel('declineNoteTitle', v)} placeholder="A note, if you'd like." />
              </div>
              <div>
                <FieldLabel>Note Placeholder</FieldLabel>
                <TextInput value={labels.declineNotePlaceholder} onChange={v => updateLabel('declineNotePlaceholder', v)} placeholder="Something for them…" />
              </div>
              <div>
                <FieldLabel>Closure Title</FieldLabel>
                <TextInput value={labels.thankYouDecline} onChange={v => updateLabel('thankYouDecline', v)} placeholder="We'll miss you." />
              </div>
              <div>
                <FieldLabel>Closure Subtitle</FieldLabel>
                <TextInput value={labels.declineSubtitle} onChange={v => updateLabel('declineSubtitle', v)} placeholder="Thank you for letting us know." />
              </div>
            </>
          ) : (
            <>
              <div>
                <FieldLabel>Heading</FieldLabel>
                <TextInput value={labels.heading} onChange={v => updateLabel('heading', v)} placeholder="Will you be joining us?" />
              </div>
              <div>
                <FieldLabel>Accept Button</FieldLabel>
                <TextInput value={labels.yesLabel} onChange={v => updateLabel('yesLabel', v)} placeholder="Yes, I'll be there!" />
              </div>
              <div>
                <FieldLabel>Decline Button</FieldLabel>
                <TextInput value={labels.noLabel} onChange={v => updateLabel('noLabel', v)} placeholder="No, I can't make it" />
              </div>
              <div>
                <FieldLabel>Submit Button</FieldLabel>
                <TextInput value={labels.submitLabel} onChange={v => updateLabel('submitLabel', v)} placeholder="Confirm" />
              </div>
              <div>
                <FieldLabel>Menu Label</FieldLabel>
                <TextInput value={labels.dietaryLabel} onChange={v => updateLabel('dietaryLabel', v)} placeholder="Menu Preference" />
              </div>
              <div>
                <FieldLabel>Children Question</FieldLabel>
                <TextInput value={labels.childrenQuestion} onChange={v => updateLabel('childrenQuestion', v)} placeholder="Will you be bringing children?" />
              </div>
              <div>
                <FieldLabel>Thank You Title</FieldLabel>
                <TextInput value={labels.thankYouTitle} onChange={v => updateLabel('thankYouTitle', v)} placeholder="Thank You!" />
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Story Editor ─────────────────────────────────────────────
function StoryEditor({ data, onChange }) {
  return (
    <div className="space-y-3">
      <div>
        <FieldLabel>Title</FieldLabel>
        <TextInput value={data?.title} onChange={v => onChange({ ...data, title: v })} placeholder="Our Story" />
      </div>
      <div>
        <FieldLabel>Body</FieldLabel>
        <TextArea value={data?.body} onChange={v => onChange({ ...data, body: v })} placeholder="We met on a beautiful summer day..." rows={5} />
      </div>
    </div>
  );
}

// ─── Countdown Editor ─────────────────────────────────────────
function CountdownEditor({ data, onChange }) {
  return (
    <div className="space-y-3">
      <div>
        <FieldLabel>Title</FieldLabel>
        <TextInput value={data?.title} onChange={v => onChange({ ...data, title: v })} placeholder="Days Until We Say I Do" />
      </div>
      <div>
        <FieldLabel>Target Date</FieldLabel>
        <input
          type="date"
          value={data?.targetDate || ''}
          onChange={e => onChange({ ...data, targetDate: e.target.value })}
          className="w-full px-2.5 py-1.5 text-xs rounded-md border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 text-gray-800 dark:text-gray-200 outline-none focus:ring-1 focus:ring-blue-400"
        />
      </div>
    </div>
  );
}

// ─── Main Widget Section Editor ───────────────────────────────
function SlideshowEditor({ data, onChange, sectionId, onImageUpload, uploadingField }) {
  const images = data.images || [];
  const isUploading = uploadingField === `${sectionId}.images`;

  const handleFile = (e) => {
    const file = e.target.files?.[0];
    if (file && onImageUpload) {
      onImageUpload(sectionId, 'images', file);
    }
    e.target.value = '';
  };

  const removeImage = (idx) => {
    onChange({ ...data, images: images.filter((_, i) => i !== idx) });
  };

  return (
    <div className="space-y-3">
      {/* Image Grid */}
      <div>
        <FieldLabel>Images</FieldLabel>
        <div className="grid grid-cols-3 gap-1.5 mb-2">
          {images.map((img, i) => (
            <div key={i} className="relative group aspect-video rounded-lg overflow-hidden border border-gray-200 dark:border-gray-700">
              <img
                src={img.startsWith('/') ? `${process.env.REACT_APP_BACKEND_URL}${img}` : img}
                alt=""
                className="w-full h-full object-cover"
              />
              <button
                onClick={() => removeImage(i)}
                className="absolute top-0.5 right-0.5 w-4 h-4 rounded-full bg-red-500 text-white text-[8px] flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                data-testid={`slideshow-remove-${i}`}
              >
                x
              </button>
            </div>
          ))}
        </div>
        <label className={`flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg border border-dashed text-[11px] cursor-pointer transition-all hover:bg-gray-50 dark:hover:bg-gray-800 ${isUploading ? 'opacity-50 pointer-events-none' : ''} text-gray-500 dark:text-gray-400 border-gray-300 dark:border-gray-700`}>
          <Upload className="w-3 h-3" />
          {isUploading ? 'Uploading...' : 'Add image'}
          <input type="file" accept="image/*" onChange={handleFile} className="hidden" data-testid="slideshow-upload" />
        </label>
      </div>

      {/* Transition */}
      <div>
        <FieldLabel>Transition</FieldLabel>
        <div className="flex gap-1.5">
          {[{ id: 'slide', label: 'Slide' }, { id: 'crossfade', label: 'Crossfade' }].map(t => (
            <button
              key={t.id}
              onClick={() => onChange({ ...data, transition: t.id })}
              className={`flex-1 px-2 py-1.5 rounded-lg text-[10px] font-medium border transition-all ${
                (data.transition || 'slide') === t.id
                  ? 'border-blue-400 bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400'
                  : 'border-gray-200 dark:border-gray-700 text-gray-500 hover:bg-gray-50 dark:hover:bg-gray-800'
              }`}
              data-testid={`slideshow-transition-${t.id}`}
            >
              {t.label}
            </button>
          ))}
        </div>
      </div>

      {/* Speed */}
      <div>
        <FieldLabel>Speed</FieldLabel>
        <div className="flex gap-1.5">
          {[{ id: 'slow', label: 'Slow' }, { id: 'medium', label: 'Medium' }, { id: 'fast', label: 'Fast' }].map(s => (
            <button
              key={s.id}
              onClick={() => onChange({ ...data, speed: s.id })}
              className={`flex-1 px-2 py-1.5 rounded-lg text-[10px] font-medium border transition-all ${
                (data.speed || 'medium') === s.id
                  ? 'border-blue-400 bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400'
                  : 'border-gray-200 dark:border-gray-700 text-gray-500 hover:bg-gray-50 dark:hover:bg-gray-800'
              }`}
              data-testid={`slideshow-speed-${s.id}`}
            >
              {s.label}
            </button>
          ))}
        </div>
      </div>

      {/* Aspect Ratio */}
      <div>
        <FieldLabel>Aspect ratio</FieldLabel>
        <div className="flex gap-1.5">
          {[{ id: '16/9', label: '16:9' }, { id: '4/3', label: '4:3' }, { id: '1/1', label: '1:1' }, { id: '3/4', label: '3:4' }].map(a => (
            <button
              key={a.id}
              onClick={() => onChange({ ...data, aspectRatio: a.id })}
              className={`flex-1 px-2 py-1.5 rounded-lg text-[10px] font-medium border transition-all ${
                (data.aspectRatio || '16/9') === a.id
                  ? 'border-blue-400 bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400'
                  : 'border-gray-200 dark:border-gray-700 text-gray-500 hover:bg-gray-50 dark:hover:bg-gray-800'
              }`}
              data-testid={`slideshow-ratio-${a.id}`}
            >
              {a.label}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

const EDITORS = {
  'event-details': EventDetailsEditor,
  'timeline': TimelineEditor,
  'venue': VenueEditor,
  'rsvp': RSVPEditor,
  'story': StoryEditor,
  'countdown': CountdownEditor,
  'slideshow': SlideshowEditor,
};

const STYLE_COLORS = [
  '#ffffff', '#f8f8f8', '#f5f0eb', '#1a1a1a', '#2A2220',
  '#1C241F', '#c8a96b', '#d4af37', '#C88F8F', '#556B5D',
  '#9b7f6b', '#3b82f6', '#8b5cf6', '#ef4444',
];

const STYLE_FONTS = [
  { id: 'serif', label: 'Serif', value: '"Playfair Display", serif' },
  { id: 'sans', label: 'Sans', value: '"Inter", sans-serif' },
  { id: 'elegant', label: 'Elegant', value: '"Cormorant Garamond", serif' },
  { id: 'modern', label: 'Modern', value: '"DM Serif Display", serif' },
  { id: 'hand', label: 'Hand', value: '"Caveat", cursive' },
];

// Phase RSVP Style 3.0 — generic collapsible "Advanced glass" sub-row for a
// material layer (Background or Surface). Pass the field key names + test IDs.
function AdvancedGlassToggle({ testId, blurKey, satKey, blurTestId, satTestId, customStyle, update }) {
  const [open, setOpen] = React.useState(false);
  const blurVal = Number.isFinite(Number(customStyle[blurKey])) ? Number(customStyle[blurKey]) : 0;
  const satVal = Number.isFinite(Number(customStyle[satKey])) ? Number(customStyle[satKey]) : 100;

  return (
    <div className="pt-1">
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className="flex items-center gap-1 text-[10px] uppercase tracking-[0.18em] text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
        data-testid={testId}
        aria-expanded={open}
      >
        <span style={{ transform: open ? 'rotate(90deg)' : 'rotate(0deg)', display: 'inline-block', transition: 'transform 160ms ease' }}>›</span>
        Advanced glass
      </button>
      {open && (
        <div className="mt-2 pl-3 border-l border-gray-100 dark:border-gray-800 space-y-3">
          <div>
            <FieldLabel>Blur</FieldLabel>
            <div className="flex items-center gap-2">
              <input type="range" min="0" max="30" step="1"
                value={blurVal}
                onChange={e => update(blurKey, Number(e.target.value))}
                className="flex-1 accent-violet-600"
                data-testid={blurTestId} />
              <span className="w-10 text-right text-[10px] font-mono text-gray-500 dark:text-gray-400 tabular-nums">
                {blurVal}px
              </span>
            </div>
          </div>
          <div>
            <FieldLabel>Saturation</FieldLabel>
            <div className="flex items-center gap-2">
              <input type="range" min="100" max="200" step="1"
                value={satVal}
                onChange={e => update(satKey, Number(e.target.value))}
                className="flex-1 accent-violet-600"
                data-testid={satTestId} />
              <span className="w-10 text-right text-[10px] font-mono text-gray-500 dark:text-gray-400 tabular-nums">
                {satVal}%
              </span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


function WidgetStyleControls({ customStyle = {}, onChange, sectionType }) {
  const update = (key, value) => onChange({ ...customStyle, [key]: value });
  const isResponseCard = sectionType === 'rsvp';

  return (
    <div className="mt-3 pt-3 border-t border-gray-100 dark:border-gray-800 space-y-3">
      <div className="flex items-center gap-1.5 mb-1">
        <Palette className="w-3 h-3 text-gray-400" />
        <span className="text-[10px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Style</span>
      </div>

      {/* Background Color */}
      <div>
        <FieldLabel>Background</FieldLabel>
        <div className="flex gap-1.5 flex-wrap items-center">
          {STYLE_COLORS.slice(0, 8).map(c => (
            <button
              key={`bg-${c}`}
              onClick={() => update('bgColor', c)}
              className={`w-5 h-5 rounded-full border-2 transition-all hover:scale-110 ${
                customStyle.bgColor === c ? 'border-blue-500 scale-110' : 'border-gray-200 dark:border-gray-600'
              }`}
              style={{ background: c }}
              data-testid={`widget-bg-${c.replace('#', '')}`}
            />
          ))}
        </div>
        <div className="flex items-center gap-1.5 mt-1.5">
          <input
            type="color"
            value={customStyle.bgColor || '#ffffff'}
            onChange={e => update('bgColor', e.target.value)}
            className="w-6 h-6 rounded cursor-pointer border border-gray-200 dark:border-gray-700 p-0.5"
          />
          <input
            type="text"
            value={customStyle.bgColor || '#ffffff'}
            onChange={e => { if (/^#[0-9a-fA-F]{0,6}$/.test(e.target.value)) update('bgColor', e.target.value); }}
            placeholder="#ffffff"
            className="flex-1 px-2 py-1 text-[10px] font-mono rounded border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 text-gray-700 dark:text-gray-300 outline-none focus:ring-1 focus:ring-blue-400"
            data-testid="widget-bg-hex"
          />
        </div>
      </div>

      {/* Text Color */}
      <div>
        <FieldLabel>Text Color</FieldLabel>
        <div className="flex gap-1.5 flex-wrap items-center">
          {['#000000', '#1a1a1a', '#333333', '#666666', '#ffffff', '#f5f0eb', '#c8a96b'].map(c => (
            <button
              key={`txt-${c}`}
              onClick={() => update('textColor', c)}
              className={`w-5 h-5 rounded-full border-2 transition-all hover:scale-110 ${
                customStyle.textColor === c ? 'border-blue-500 scale-110' : 'border-gray-200 dark:border-gray-600'
              }`}
              style={{ background: c }}
            />
          ))}
        </div>
        <div className="flex items-center gap-1.5 mt-1.5">
          <input
            type="color"
            value={customStyle.textColor || '#000000'}
            onChange={e => update('textColor', e.target.value)}
            className="w-6 h-6 rounded cursor-pointer border border-gray-200 dark:border-gray-700 p-0.5"
          />
          <input
            type="text"
            value={customStyle.textColor || '#000000'}
            onChange={e => { if (/^#[0-9a-fA-F]{0,6}$/.test(e.target.value)) update('textColor', e.target.value); }}
            placeholder="#000000"
            className="flex-1 px-2 py-1 text-[10px] font-mono rounded border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 text-gray-700 dark:text-gray-300 outline-none focus:ring-1 focus:ring-blue-400"
            data-testid="widget-text-hex"
          />
        </div>
      </div>

      {/* Accent / Icon Color */}
      <div>
        <FieldLabel>Accent & Icons</FieldLabel>
        <div className="flex gap-1.5 flex-wrap items-center">
          {['#c8a96b', '#d4af37', '#C88F8F', '#556B5D', '#9b7f6b', '#3b82f6', '#8b5cf6', '#ef4444'].map(c => (
            <button
              key={`acc-${c}`}
              onClick={() => update('accentColor', c)}
              className={`w-5 h-5 rounded-full border-2 transition-all hover:scale-110 ${
                customStyle.accentColor === c ? 'border-blue-500 scale-110' : 'border-gray-200 dark:border-gray-600'
              }`}
              style={{ background: c }}
            />
          ))}
        </div>
        <div className="flex items-center gap-1.5 mt-1.5">
          <input
            type="color"
            value={customStyle.accentColor || '#c8a96b'}
            onChange={e => update('accentColor', e.target.value)}
            className="w-6 h-6 rounded cursor-pointer border border-gray-200 dark:border-gray-700 p-0.5"
          />
          <input
            type="text"
            value={customStyle.accentColor || '#c8a96b'}
            onChange={e => { if (/^#[0-9a-fA-F]{0,6}$/.test(e.target.value)) update('accentColor', e.target.value); }}
            placeholder="#c8a96b"
            className="flex-1 px-2 py-1 text-[10px] font-mono rounded border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 text-gray-700 dark:text-gray-300 outline-none focus:ring-1 focus:ring-blue-400"
            data-testid="widget-accent-hex"
          />
        </div>
      </div>

      {/* Button Color */}
      <div>
        <FieldLabel>Button Color</FieldLabel>
        <div className="flex items-center gap-1.5">
          <input
            type="color"
            value={customStyle.buttonColor || customStyle.accentColor || '#c8a96b'}
            onChange={e => update('buttonColor', e.target.value)}
            className="w-6 h-6 rounded cursor-pointer border border-gray-200 dark:border-gray-700 p-0.5"
          />
          <input
            type="text"
            value={customStyle.buttonColor || ''}
            onChange={e => { if (/^#[0-9a-fA-F]{0,6}$/.test(e.target.value)) update('buttonColor', e.target.value); }}
            placeholder="Same as accent"
            className="flex-1 px-2 py-1 text-[10px] font-mono rounded border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 text-gray-700 dark:text-gray-300 outline-none focus:ring-1 focus:ring-blue-400"
            data-testid="widget-button-color-hex"
          />
        </div>
      </div>

      {/* Phase RSVP Style 3.0 — Response Card only.
          Three real layers + form:
            • BACKGROUND  (NEW outer layer, defaults to none)
            • SURFACE     (inner card; legacy controls renamed back to Surface)
            • FORM        (content column)
            • BUTTON      (submit button shadow)
          Each material group has a collapsible "Advanced glass" subsection
          with blur + saturation. All defaults are legacy-safe. */}
      {isResponseCard && (
        <>
          {/* ─── BACKGROUND ─── */}
          <div className="pt-2">
            <div className="text-[10px] uppercase tracking-[0.18em] text-gray-400 dark:text-gray-500 mb-2">Background</div>

            <div className="mb-3">
              <FieldLabel>Background type</FieldLabel>
              <div className="flex gap-1.5">
                {['none', 'color'].map(t => (
                  <button
                    key={t}
                    type="button"
                    onClick={() => update('bgLayerType', t === 'none' ? undefined : t)}
                    data-testid={`rsvp-bg-type-${t}`}
                    className={`flex-1 px-2 py-1.5 rounded text-[11px] capitalize ${(customStyle.bgLayerType || 'none') === t ? 'bg-violet-600 text-white' : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 hover:bg-gray-200'}`}
                  >
                    {t}
                  </button>
                ))}
              </div>
            </div>

            {(customStyle.bgLayerType === 'color') && (
              <>
                <div className="mb-3">
                  <FieldLabel>Background opacity</FieldLabel>
                  <div className="flex items-center gap-2">
                    <input type="range" min="0" max="100" step="1"
                      value={Number.isFinite(Number(customStyle.bgLayerOpacity)) ? Number(customStyle.bgLayerOpacity) : 100}
                      onChange={e => update('bgLayerOpacity', Number(e.target.value))}
                      className="flex-1 accent-violet-600"
                      data-testid="rsvp-bg-opacity-slider" />
                    <span className="w-8 text-right text-[10px] font-mono text-gray-500 dark:text-gray-400 tabular-nums" data-testid="rsvp-bg-opacity-value">
                      {Number.isFinite(Number(customStyle.bgLayerOpacity)) ? Number(customStyle.bgLayerOpacity) : 100}
                    </span>
                  </div>
                </div>

                <div className="mb-3">
                  <FieldLabel>Background width</FieldLabel>
                  <div className="flex items-center gap-2">
                    <input type="range" min="70" max="100" step="1"
                      value={Number.isFinite(Number(customStyle.bgLayerWidthPercent)) ? Number(customStyle.bgLayerWidthPercent) : 100}
                      onChange={e => update('bgLayerWidthPercent', Number(e.target.value))}
                      className="flex-1 accent-violet-600"
                      data-testid="rsvp-bg-width-slider" />
                    <span className="w-10 text-right text-[10px] font-mono text-gray-500 dark:text-gray-400 tabular-nums" data-testid="rsvp-bg-width-value">
                      {Number.isFinite(Number(customStyle.bgLayerWidthPercent)) ? Number(customStyle.bgLayerWidthPercent) : 100}%
                    </span>
                  </div>
                </div>

                <div className="mb-2">
                  <FieldLabel>Background shadow</FieldLabel>
                  <div className="flex items-center gap-2">
                    <input type="range" min="0" max="100" step="1"
                      value={Number(customStyle.bgLayerShadowStrength) || 0}
                      onChange={e => update('bgLayerShadowStrength', Number(e.target.value))}
                      className="flex-1 accent-violet-600"
                      data-testid="rsvp-bg-shadow-slider" />
                    <span className="w-8 text-right text-[10px] font-mono text-gray-500 dark:text-gray-400 tabular-nums" data-testid="rsvp-bg-shadow-value">
                      {Number(customStyle.bgLayerShadowStrength) || 0}
                    </span>
                  </div>
                </div>

                <div className="mb-2">
                  <FieldLabel>Background radius</FieldLabel>
                  <div className="flex items-center gap-2">
                    <input type="range" min="0" max="64" step="1"
                      value={Number.isFinite(Number(customStyle.bgLayerRadius)) ? Number(customStyle.bgLayerRadius) : 20}
                      onChange={e => update('bgLayerRadius', Number(e.target.value))}
                      className="flex-1 accent-violet-600"
                      data-testid="rsvp-bg-radius-slider" />
                    <span className="w-10 text-right text-[10px] font-mono text-gray-500 dark:text-gray-400 tabular-nums" data-testid="rsvp-bg-radius-value">
                      {Number.isFinite(Number(customStyle.bgLayerRadius)) ? Number(customStyle.bgLayerRadius) : 20}px
                    </span>
                  </div>
                </div>

                <AdvancedGlassToggle
                  testId="rsvp-bg-glass-toggle"
                  blurKey="bgLayerBackdropBlur"
                  satKey="bgLayerBackdropSaturate"
                  blurTestId="rsvp-bg-blur-slider"
                  satTestId="rsvp-bg-saturate-slider"
                  customStyle={customStyle}
                  update={update}
                />
              </>
            )}
          </div>

          {/* ─── SURFACE ─── */}
          <div className="pt-2">
            <div className="text-[10px] uppercase tracking-[0.18em] text-gray-400 dark:text-gray-500 mb-2">Surface</div>

            <div className="mb-3">
              <FieldLabel>Surface type</FieldLabel>
              <div className="flex gap-1.5">
                {['none', 'color'].map(t => (
                  <button
                    key={t}
                    type="button"
                    onClick={() => update('surfaceType', t === 'color' ? undefined : t)}
                    data-testid={`rsvp-surface-type-${t}`}
                    className={`flex-1 px-2 py-1.5 rounded text-[11px] capitalize ${(customStyle.surfaceType || 'color') === t ? 'bg-violet-600 text-white' : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 hover:bg-gray-200'}`}
                  >
                    {t}
                  </button>
                ))}
              </div>
            </div>

            {(customStyle.surfaceType || 'color') === 'color' && (
              <>
                <div className="mb-3">
                  <FieldLabel>Surface opacity</FieldLabel>
                  <div className="flex items-center gap-2">
                    <input type="range" min="0" max="100" step="1"
                      value={Number.isFinite(Number(customStyle.cardSurfaceOpacity)) ? Number(customStyle.cardSurfaceOpacity) : 100}
                      onChange={e => update('cardSurfaceOpacity', Number(e.target.value))}
                      className="flex-1 accent-violet-600"
                      data-testid="rsvp-surface-opacity-slider" />
                    <span className="w-8 text-right text-[10px] font-mono text-gray-500 dark:text-gray-400 tabular-nums" data-testid="rsvp-surface-opacity-value">
                      {Number.isFinite(Number(customStyle.cardSurfaceOpacity)) ? Number(customStyle.cardSurfaceOpacity) : 100}
                    </span>
                  </div>
                </div>

                <div className="mb-3">
                  <FieldLabel>Surface width</FieldLabel>
                  <div className="flex items-center gap-2">
                    <input type="range" min="70" max="100" step="1"
                      value={Number.isFinite(Number(customStyle.cardSurfaceWidthPercent)) ? Number(customStyle.cardSurfaceWidthPercent) : 100}
                      onChange={e => update('cardSurfaceWidthPercent', Number(e.target.value))}
                      className="flex-1 accent-violet-600"
                      data-testid="rsvp-surface-width-slider" />
                    <span className="w-10 text-right text-[10px] font-mono text-gray-500 dark:text-gray-400 tabular-nums" data-testid="rsvp-surface-width-value">
                      {Number.isFinite(Number(customStyle.cardSurfaceWidthPercent)) ? Number(customStyle.cardSurfaceWidthPercent) : 100}%
                    </span>
                  </div>
                </div>

                <div className="mb-2">
                  <FieldLabel>Surface shadow</FieldLabel>
                  <div className="flex items-center gap-2">
                    <input type="range" min="0" max="100" step="1"
                      value={Number(customStyle.cardSurfaceShadowStrength) || 0}
                      onChange={e => update('cardSurfaceShadowStrength', Number(e.target.value))}
                      className="flex-1 accent-violet-600"
                      data-testid="rsvp-surface-shadow-slider" />
                    <span className="w-8 text-right text-[10px] font-mono text-gray-500 dark:text-gray-400 tabular-nums" data-testid="rsvp-surface-shadow-value">
                      {Number(customStyle.cardSurfaceShadowStrength) || 0}
                    </span>
                  </div>
                </div>

                <div className="mb-2">
                  <FieldLabel>Surface radius</FieldLabel>
                  <div className="flex items-center gap-2">
                    <input type="range" min="0" max="64" step="1"
                      value={Number.isFinite(Number(customStyle.cardSurfaceRadius)) ? Number(customStyle.cardSurfaceRadius) : 20}
                      onChange={e => update('cardSurfaceRadius', Number(e.target.value))}
                      className="flex-1 accent-violet-600"
                      data-testid="rsvp-surface-radius-slider" />
                    <span className="w-10 text-right text-[10px] font-mono text-gray-500 dark:text-gray-400 tabular-nums" data-testid="rsvp-surface-radius-value">
                      {Number.isFinite(Number(customStyle.cardSurfaceRadius)) ? Number(customStyle.cardSurfaceRadius) : 20}px
                    </span>
                  </div>
                </div>

                <AdvancedGlassToggle
                  testId="rsvp-surface-glass-toggle"
                  blurKey="cardBackdropBlur"
                  satKey="cardBackdropSaturate"
                  blurTestId="rsvp-surface-blur-slider"
                  satTestId="rsvp-surface-saturate-slider"
                  customStyle={customStyle}
                  update={update}
                />
              </>
            )}
          </div>

          {/* ─── FORM ─── */}
          <div className="pt-2">
            <div className="text-[10px] uppercase tracking-[0.18em] text-gray-400 dark:text-gray-500 mb-2">Form</div>
            <FieldLabel>Form width</FieldLabel>
            <div className="flex items-center gap-2">
              <input type="range" min="70" max="100" step="1"
                value={Number.isFinite(Number(customStyle.cardWidthPercent)) ? Number(customStyle.cardWidthPercent) : 100}
                onChange={e => update('cardWidthPercent', Number(e.target.value))}
                className="flex-1 accent-violet-600"
                data-testid="rsvp-form-width-slider" />
              <span className="w-10 text-right text-[10px] font-mono text-gray-500 dark:text-gray-400 tabular-nums" data-testid="rsvp-form-width-value">
                {Number.isFinite(Number(customStyle.cardWidthPercent)) ? Number(customStyle.cardWidthPercent) : 100}%
              </span>
            </div>
          </div>

          {/* ─── BUTTON ─── */}
          <div className="pt-2">
            <div className="text-[10px] uppercase tracking-[0.18em] text-gray-400 dark:text-gray-500 mb-2">Button</div>
            <FieldLabel>Button shadow</FieldLabel>
            <div className="flex items-center gap-2">
              <input type="range" min="0" max="100" step="1"
                value={Number(customStyle.buttonShadowStrength) || 0}
                onChange={e => update('buttonShadowStrength', Number(e.target.value))}
                className="flex-1 accent-violet-600"
                data-testid="widget-button-shadow-slider" />
              <span className="w-8 text-right text-[10px] font-mono text-gray-500 dark:text-gray-400 tabular-nums" data-testid="widget-button-shadow-value">
                {Number(customStyle.buttonShadowStrength) || 0}
              </span>
            </div>
          </div>
        </>
      )}

      {/* Font */}
      <div>
        <FieldLabel>Font</FieldLabel>
        <div className="flex gap-1 flex-wrap">
          {STYLE_FONTS.map(f => (
            <button
              key={f.id}
              onClick={() => update('fontFamily', f.value)}
              className={`px-2 py-1 rounded text-[10px] font-medium transition-all ${
                customStyle.fontFamily === f.value
                  ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 ring-1 ring-blue-300'
                  : 'bg-gray-50 dark:bg-gray-800 text-gray-600 dark:text-gray-400 hover:bg-gray-100'
              }`}
              style={{ fontFamily: f.value }}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

export default function WidgetSectionEditor({ section, onUpdateData, onImageUpload, uploadingField, onMoveUp, onMoveDown, onRemove, variants, onVariantChange, eventId, token }) {
  const [collapsed, setCollapsed] = React.useState(false);
  const Editor = EDITORS[section.type];
  if (!Editor) return null;

  const data = section.data || {};
  // Phase Response Card 1.0 Lite — Studio rename: RSVP section header reads
  // "Edit Response Card" instead of "Edit RSVP". Backend identifier still
  // `section.type === 'rsvp'`.
  const isResponseCard = section.type === 'rsvp';
  const headerLabel = isResponseCard
    ? 'Edit Response Card'
    : `Edit ${section.label || section.type}`;

  return (
    <div
      className="rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 overflow-hidden"
      data-testid={`widget-editor-${section.id}`}
    >
      <div className="flex items-center justify-between px-3 py-2.5 bg-gray-50 dark:bg-gray-900/50 hover:bg-gray-100 dark:hover:bg-gray-800/50 transition-colors">
        <button
          onClick={() => setCollapsed(c => !c)}
          className="flex items-center gap-2 flex-1 min-w-0"
          data-testid={`widget-editor-toggle-${section.id}`}
        >
          <h3 className="text-[10px] font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider truncate">
            {headerLabel}
          </h3>
          <ChevronDown className={`w-3.5 h-3.5 text-gray-400 transition-transform duration-200 flex-shrink-0 ${collapsed ? '' : 'rotate-180'}`} />
        </button>
        {(onMoveUp || onMoveDown || onRemove) && (
          <div className="flex items-center gap-0.5 ml-2 flex-shrink-0">
            {onMoveUp && (
              <button onClick={onMoveUp} className="p-1 rounded hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-400 hover:text-gray-600" title="Move up" data-testid={`widget-move-up-${section.id}`}>
                <ArrowUp className="w-3 h-3" />
              </button>
            )}
            {onMoveDown && (
              <button onClick={onMoveDown} className="p-1 rounded hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-400 hover:text-gray-600" title="Move down" data-testid={`widget-move-down-${section.id}`}>
                <ArrowDown className="w-3 h-3" />
              </button>
            )}
            {onRemove && (
              <button onClick={onRemove} className="p-1 rounded hover:bg-red-50 dark:hover:bg-red-900/20 text-gray-400 hover:text-red-500" title="Remove" data-testid={`widget-remove-${section.id}`}>
                <Trash2 className="w-3 h-3" />
              </button>
            )}
          </div>
        )}
      </div>
      {!collapsed && (
        <div className="p-3 border-t border-gray-100 dark:border-gray-800">
          {/* Variant Picker */}
          {variants && variants.length > 1 && onVariantChange && (
            <div className="mb-3 pb-3 border-b border-gray-100 dark:border-gray-800">
              <span className="text-[10px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider block mb-1.5">Layout</span>
              <div className="flex flex-wrap gap-1">
                {variants.map(v => (
                  <button
                    key={v.id}
                    onClick={() => onVariantChange(section.id, v.id)}
                    className={`px-2.5 py-1 rounded-md text-[10px] font-medium transition-all ${
                      section.variant === v.id
                        ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 ring-1 ring-blue-300'
                        : 'bg-gray-50 dark:bg-gray-800 text-gray-600 dark:text-gray-400 hover:bg-gray-100'
                    }`}
                    data-testid={`variant-${v.id}`}
                  >
                    {v.label}
                  </button>
                ))}
              </div>
            </div>
          )}
          <Editor data={data} onChange={(newData) => onUpdateData(section.id, newData)} variant={section.variant} sectionId={section.id} onImageUpload={onImageUpload} uploadingField={uploadingField} />
          <WidgetStyleControls
            customStyle={data.customStyle || {}}
            onChange={(newStyle) => onUpdateData(section.id, { ...data, customStyle: newStyle })}
            sectionType={section.type}
          />
          {/* Phase Response Card 1.0 Lite — collapsed by default. Only mounted
              for RSVP sections; eventId + token required for the existing
              upload endpoint when a custom image is selected. */}
          {isResponseCard && eventId && token && (
            <div className="mt-3">
              <ResponseCardBackgroundPanel
                eventId={eventId}
                token={token}
                background={data.background}
                onChange={(nextBg) => onUpdateData(section.id, { ...data, background: nextBg })}
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
}
